import React, { useState, useEffect, useRef } from 'react';
import { FiBell, FiCheck } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_URL, authHeaders } from '../../services/api';

const NotificationBell = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Fetch both count and list if open
    const fetchNotifications = async () => {
        if (!isAuthenticated || !user?.id) return;
        try {
            // Fetch unread count
            const countRes = await fetch(`${API_URL}/api/notifications/${user.id}/unread_count`, { headers: authHeaders() });
            if (countRes.ok) {
                const countData = await countRes.json();
                setUnreadCount(countData.unread_count || 0);
            }

            // Fetch list if dropdown is open (or just fetch them anyway to be ready)
            const listRes = await fetch(`${API_URL}/api/notifications/${user.id}`, { headers: authHeaders() });
            if (listRes.ok) {
                const listData = await listRes.json();
                setNotifications(listData);
            }
        } catch (error) {
            console.error("Error fetching notifications", error);
        }
    };

    // Polling every 30 seconds
    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, user?.id]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const markAsRead = async (id, enlace) => {
        try {
            await fetch(`${API_URL}/api/notifications/${id}/read`, { method: 'PUT', headers: authHeaders() });

            // Optimistic update
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, leida: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));

            setIsOpen(false);
            if (enlace) {
                navigate(enlace);
            }
        } catch (error) {
            console.error("Error marking as read", error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await fetch(`${API_URL}/api/notifications/${user.id}/read_all`, { method: 'PUT', headers: authHeaders() });
            setNotifications(prev => prev.map(n => ({ ...n, leida: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error("Error marking all as read");
        }
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            fetchNotifications(); // Refresh when opening
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return `hace ${diffInSeconds} seg`;

        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) return `hace ${diffInMinutes} min`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `hace ${diffInHours} h`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays === 1) return `ayer`;
        if (diffInDays < 7) return `hace ${diffInDays} d`;

        return date.toLocaleDateString();
    };

    if (!isAuthenticated) return null;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={toggleDropdown}
                className="p-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors duration-200 relative text-surface-600 dark:text-surface-300"
                aria-label="Notificaciones"
            >
                <FiBell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1.5 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center text-[10px] font-bold text-white border-2 border-white dark:border-surface-950">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-surface-900 rounded-2xl shadow-xl shadow-surface-900/10 border border-surface-200 dark:border-surface-800 z-[100] overflow-hidden animate-scale-in origin-top-right">
                    <div className="flex items-center justify-between p-4 border-b border-surface-100 dark:border-surface-800 bg-surface-50/50 dark:bg-surface-950/50">
                        <h3 className="font-bold text-surface-900 dark:text-white">Notificaciones</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="text-xs text-brand-600 dark:text-brand-400 font-medium hover:underline flex items-center gap-1"
                            >
                                <FiCheck /> Marcar todas leídas
                            </button>
                        )}
                    </div>

                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center text-surface-500 dark:text-surface-400 text-sm">
                                <div className="mx-auto w-12 h-12 rounded-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center mb-3">
                                    <FiBell className="w-5 h-5 opacity-50" />
                                </div>
                                No tienes notificaciones nuevas
                            </div>
                        ) : (
                            <div className="divide-y divide-surface-100 dark:divide-surface-800">
                                {notifications.map(notif => (
                                    <button
                                        key={notif.id}
                                        onClick={() => markAsRead(notif.id, notif.enlace)}
                                        className={`w-full text-left p-4 hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors flex gap-3 ${!notif.leida ? 'bg-brand-50/30 dark:bg-brand-900/10' : ''}`}
                                    >
                                        <div className="relative mt-1">
                                            <div className={`w-2 h-2 rounded-full mt-1.5 ${!notif.leida ? 'bg-brand-500' : 'bg-transparent'}`}></div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm ${!notif.leida ? 'font-semibold text-surface-900 dark:text-white' : 'font-medium text-surface-700 dark:text-surface-300'}`}>
                                                {notif.titulo}
                                            </p>
                                            <p className={`text-xs mt-0.5 line-clamp-2 ${!notif.leida ? 'text-surface-600 dark:text-surface-400' : 'text-surface-500'}`}>
                                                {notif.mensaje}
                                            </p>
                                            <p className="text-[10px] text-surface-400 mt-1.5 uppercase font-medium tracking-wider">
                                                {formatTimeAgo(notif.created_at)}
                                            </p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
